import { useEffect, useState, useCallback } from 'react';
import ProjectSetup from '@/components/Info/ProjectSetup';
import SpaceConfiguration from '@/components/Analysis/FunctionsSpaces/SpaceConfiguration';
import NoiseAnalysis from '@/components/Analysis/Noise/NoiseAnalysis';
import ThreeScene from '@/components/Analysis/DaylightViews/ThreeScene';
import MaterialsAssumptions from '@/components/Analysis/MaterialsAssumptions/MaterialsAssumptions';
import { FormaTab, FormaTabs } from '@/components/UI/FormaTabs';
import styles from './styles.module.scss';
import { useWorkflowStore } from '@/store/workflowStore';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveBanner } from '../UI/WeaveBanner/WeaveBanner';
import { BioScoreResultsPanel } from '@/components/Results/BioScoreResultsPanel';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import { StageBar } from '@/components/StageBar/StageBar';
import { WorkflowStage } from '@/types/palette';

type ScreenMode = 'info' | 'analysis';

export type AnalysisTab = {
	id: string;
	label: string;
	section: number | null;
};

const ANALYSIS_TABS: AnalysisTab[] = [
	{ id: 'functions-spaces', label: 'Functions & Spaces', section: 2 },
	{ id: 'windows-openings', label: 'Windows & Terrain', section: 9 },
	{ id: 'daylight-views', label: 'Daylight & Views', section: 4 },
	{ id: 'materials-assumptions', label: 'Materials & Assumptions', section: 8 },
	{ id: 'noise', label: 'Noise', section: 3 },
	{ id: 'bioscore-results', label: 'BioScore & Results', section: 5 },
];
const ANALYSIS_TAB_IDS = new Set(ANALYSIS_TABS.map((tab) => tab.id));

const Home = function () {
	const { homeContainer, contentArea, analysisContentArea, loadNotice } = styles;
	const [screen, setScreen] = useState<ScreenMode>('info');
	const [activeTabId, setActiveTabId] = useState<string>(ANALYSIS_TABS[0].id);
	const {
		workflowData,
		setWorkflowData,
		refresh: refreshWorkflow,
		error,
		currentStage,
		setCurrentStage,
		objectLock,
		setObjectLock,
		activeObjectContext,
		setActiveObjectContext,
	} = useWorkflowStore();
	const { setIsHoleCreationMode } = useThreeSceneStore();
	const {
		setIsLandmarkSelectionMode,
		setIsPaintMode,
		setIsPaintDeletionMode,
	} = useViewFeaturesStore();
	const currentTab = ANALYSIS_TABS.find((tab) => tab.id === activeTabId) || ANALYSIS_TABS[0];
	const activeSection = currentTab?.section;

	const handleDataChange = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(data: any) => {
			if (!activeSection) return;
			setWorkflowData(activeSection, data);
		},
		[activeSection, setWorkflowData]
	);

	useEffect(() => {
		refreshWorkflow();
	}, [refreshWorkflow]);

	useEffect(() => {
		setIsHoleCreationMode(false);
		setIsPaintMode(false);
		setIsPaintDeletionMode(false);
		setIsLandmarkSelectionMode(false);
	}, [activeTabId, setIsHoleCreationMode, setIsLandmarkSelectionMode, setIsPaintMode, setIsPaintDeletionMode]);

	const renderTabContent = (tabId: string) => {
		const tab = ANALYSIS_TABS.find((item) => item.id === tabId);
		if (!tab) return null;

		if (tab.id === 'functions-spaces') {
			return (
				<SpaceConfiguration
					onDataChange={handleDataChange}
					workflowData={workflowData}
					sectionLabel={tab.label}
					activeSection={2}
					currentStage={currentStage}
					objectLockEnabled={objectLock}
					onActiveObjectChange={(payload) => setActiveObjectContext(payload)}
				/>
			);
		}

		if (tab.id === 'windows-openings') {
			return (
				<ThreeScene
					key={tab.id} // Force remount when switching back to this tab
					onDataChange={handleDataChange}
					workflowData={workflowData}
					sectionLabel={tab.label}
					activeSection={3}
					tab={tab}
					currentStage={currentStage}
				/>
			);
		}

		if (tab.id === 'daylight-views') {
			return (
				<ThreeScene
					key={tab.id} // Force remount when switching to ensure clean state
					onDataChange={handleDataChange}
					workflowData={workflowData}
					sectionLabel={tab.label}
					activeSection={4}
					tab={tab}
					currentStage={currentStage}
				/>
			);
		}

		if (tab.id === 'materials-assumptions') {
			return (
				<MaterialsAssumptions
					onDataChange={handleDataChange}
					workflowData={workflowData}
					sectionLabel={tab.label}
					activeSection={8}
					currentStage={currentStage}
				/>
			);
		}

		if (tab.id === 'noise') {
			return (
				<NoiseAnalysis
					onDataChange={handleDataChange}
					workflowData={workflowData}
					sectionLabel={tab.label}
					activeSection={3}
					currentStage={currentStage}
				/>
			);
		}

		return (
			<BioScoreResultsPanel
				onDataChange={handleDataChange}
				workflowData={workflowData}
				sectionLabel={tab.label}
				activeSection={5}
				currentStage={currentStage}
			/>
		);
	};

	return (
		<div className={homeContainer}>
			{error && (
				<div className={loadNotice}>
					<WeaveBanner
						variant="forma-neutral"
						text={
							<>
								<span>We couldn’t load the data right now. Please try again.</span>{' '}
								<WeaveButton variant="outlined" onClick={refreshWorkflow}>
									Retry
								</WeaveButton>
							</>
						}
					/>
				</div>
			)}
			{screen === 'info' ? (
				<>
					<div className={contentArea}>
						<ProjectSetup
							onDataChange={handleDataChange}
							workflowData={workflowData}
							sectionLabel="Info"
							activeSection={1}
						/>
					</div>
					<div className={styles.bottomActionsInfo}>
						<WeaveButton variant="solid" onClick={() => setScreen('analysis')}>
							Go to Analysis
						</WeaveButton>
					</div>
				</>
			) : (
				<>
					<div className={`${contentArea} ${analysisContentArea}`}>
						<div className={styles.stickyHeader}>
							<FormaTabs
								className={styles.tabsComponent}
								gap={8}
								density="medium"
								onChange={(event: unknown) => {
									const target = event as {
										nativeEvent?: { detail?: { tabContentId?: string } };
										detail?: { tabContentId?: string };
									};
									const newTabId =
										target?.nativeEvent?.detail?.tabContentId ??
										target?.detail?.tabContentId;
									if (newTabId && ANALYSIS_TAB_IDS.has(newTabId)) {
										setActiveTabId(newTabId);
									}
								}}
							>
								{ANALYSIS_TABS.map((tab) => (
									<FormaTab
										key={tab.id}
										for={tab.id}
										label={tab.label}
										onClick={() => setActiveTabId(tab.id)}
									/>
								))}
								{ANALYSIS_TABS.map((tab) => (
									<div
										key={`${tab.id}-content`}
										id={tab.id}
										slot="content"
										className={styles.tabsPlaceholderContent}
									/>
								))}
							</FormaTabs>
							<StageBar
								currentStage={currentStage}
								onChange={(stage: WorkflowStage) => {
									setCurrentStage(stage);
								}}
							/>
						</div>
						<div className={styles.analysisPanel}>{renderTabContent(activeTabId)}</div>
					</div>
					<div className={styles.bottomActionsAnalysis}>
						<WeaveButton onClick={() => setScreen('info')}>← Back</WeaveButton>
						{activeTabId === 'functions-spaces' ? (
							<>
								<div className={styles.activeObjectInfo}>
									<div className={styles.activeObjectLabel}>
										Working on:{' '}
										<span className={styles.activeObjectValue}>
											{activeObjectContext?.contextKey ?? 'No object selected'}
										</span>
									</div>
									<div className={styles.activeObjectMeta}>
										{activeObjectContext
											? `${activeObjectContext.functionSummary} (${activeObjectContext.selectedPath})`
											: 'Select one object and refresh in Functions & Spaces'}
									</div>
								</div>
								<WeaveButton
									variant={objectLock ? 'solid' : 'outlined'}
									onClick={() => setObjectLock(!objectLock)}
								>
									{objectLock ? 'Object Lock On' : 'Object Lock Off'}
								</WeaveButton>
							</>
						) : null}
					</div>
				</>
			)}
		</div>
	);
};

export default Home;
